import React from 'react'
import PropTypes from 'prop-types'

/**
 * ConnectionSummary Component
 *
 * Displays a summary widget showing connection statistics
 *
 * Props:
 * - summary: Summary object {total_can_view, adults, minors, pending, tree_only}
 * - onManageClick: Handler for "Manage Connections" button
 * - onAddMinorClick: Handler for "Add Child" button
 */
function ConnectionSummary({ summary, onManageClick, onAddMinorClick }) {
  const { total_can_view, adults, minors, pending, tree_only } = summary || {
    total_can_view: 0,
    adults: 0,
    minors: 0,
    pending: 0,
    tree_only: 0
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">👨‍👩‍👧‍👦</span>
        <h3 className="text-xl font-bold text-gray-800">Connection Summary</h3>
      </div>

      {/* Summary Stats */}
      <div className="space-y-3 mb-6">
        {/* Adults */}
        <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <span className="text-xl">✅</span>
            <span className="font-semibold text-gray-700">Adults with accounts</span>
          </div>
          <span className="text-2xl font-bold text-green-700">{adults}</span>
        </div>

        {/* Minors */}
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <span className="text-xl">👶</span>
            <span className="font-semibold text-gray-700">Children (via parents)</span>
          </div>
          <span className="text-2xl font-bold text-blue-700">{minors}</span>
        </div>

        {/* Divider */}
        <div className="border-t-2 border-gray-300 my-4"></div>

        {/* Total Can View */}
        <div className="flex items-center justify-between p-4 bg-[#F5F5DC] rounded-lg border-2 border-[#556B2F]">
          <div>
            <div className="text-sm text-gray-600 mb-1">Can see your stories:</div>
            <div className="text-lg font-bold text-[#556B2F]">
              {total_can_view} {total_can_view === 1 ? 'person' : 'people'}
            </div>
          </div>
          <div className="text-4xl font-bold text-[#556B2F]">{total_can_view}</div>
        </div>

        {/* Pending (if any) */}
        {pending > 0 && (
          <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-2">
              <span className="text-xl">⏳</span>
              <span className="font-semibold text-gray-700">Pending invitations</span>
            </div>
            <span className="text-2xl font-bold text-yellow-700">{pending}</span>
          </div>
        )}

        {/* Tree Only (if any) */}
        {tree_only > 0 && (
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-xl">🌳</span>
              <span className="font-semibold text-gray-700">Tree only</span>
            </div>
            <span className="text-2xl font-bold text-gray-700">{tree_only}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="space-y-2">
        {onAddMinorClick && (
          <button
            onClick={onAddMinorClick}
            className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-semibold flex items-center justify-center gap-2"
          >
            <span className="text-xl">👶</span>
            <span>Add Child</span>
          </button>
        )}

        {onManageClick && (
          <button
            onClick={onManageClick}
            className="w-full px-4 py-3 bg-[#556B2F] text-white rounded-lg hover:bg-[#4A5D23] transition-colors font-semibold"
          >
            Manage Connections →
          </button>
        )}
      </div>

      {/* Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Connected people can view and contribute to family stories
        </p>
      </div>
    </div>
  )
}

ConnectionSummary.propTypes = {
  summary: PropTypes.shape({
    total_can_view: PropTypes.number,
    adults: PropTypes.number,
    minors: PropTypes.number,
    pending: PropTypes.number,
    tree_only: PropTypes.number
  }),
  onManageClick: PropTypes.func,
  onAddMinorClick: PropTypes.func
}

export default ConnectionSummary
